import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { LuMenu } from "react-icons/lu";
import ReactMarkdown from "react-markdown";
import { Link, useParams } from "react-router-dom";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { typography } from "../../design/tokens";
import { trackPageview } from "../../utils/analyticsUtils";
import { fetchWithRetry } from "../../utils/retry.util";
import {
  SideMenuBehavior,
  useIsSideMenuOpen,
  useSideMenuActions,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import SideMenuPanel from "../sideMenu/SideMenuPanel";
import { WelcomeAppBar } from "../welcome/components/WelcomeAppBar";
import { WelcomeFooter } from "../welcome/components/WelcomeFooter";

interface BlogPostMeta {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  coverImage: string;
}

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [meta, setMeta] = useState<BlogPostMeta | null>(null);
  const [content, setContent] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const isUserSignedIn = useIsUserSignedIn();
  const isCompactView = useIsCompactView();
  const { open: openSideMenu, close: closeSideMenu } = useSideMenuActions();
  const isSideMenuOpen = useIsSideMenuOpen();
  const sideMenuBehavior = useSideMenuBehavior();

  const extractTextFromChildren = (children: any): string => {
    if (typeof children === "string") {
      return children;
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromChildren).join("");
    }
    if (children?.props?.children) {
      return extractTextFromChildren(children.props.children);
    }
    return "";
  };

  const handleCopyCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  useEffect(() => {
    if (slug) {
      trackPageview(`Blog: ${slug}`);
    }
  }, [slug]);

  useEffect(() => {
    const fetchBlogPost = async () => {
      if (!slug) {
        setError("No blog post slug provided");
        setLoading(false);
        return;
      }

      try {
        // First, fetch the blog index to get the dateFolder
        const indexResponse = await fetchWithRetry("/blog/index.json");
        if (!indexResponse.ok) {
          throw new Error("Failed to load blog index");
        }
        const posts = await indexResponse.json();
        const postInfo = posts.find((p: any) => p.slug === slug);

        if (!postInfo) {
          throw new Error("Blog post not found");
        }

        // Fetch meta.json
        const metaResponse = await fetchWithRetry(
          `/blog/${postInfo.dateFolder}/meta.json`
        );
        if (!metaResponse.ok) {
          throw new Error("Failed to load blog post metadata");
        }
        const metaData = await metaResponse.json();
        setMeta(metaData);

        // Fetch content.md
        const contentResponse = await fetchWithRetry(
          `/blog/${postInfo.dateFolder}/content.md`
        );
        if (!contentResponse.ok) {
          throw new Error("Failed to load blog post content");
        }
        const contentData = await contentResponse.text();
        setContent(contentData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchBlogPost();
  }, [slug]);

  if (loading || error || !meta) {
    return (
      <>
        <Helmet>
          <title>Blog | Darvin</title>
        </Helmet>
        <div className="min-h-screen flex">
          {isUserSignedIn && !isCompactView && <SideMenuPanel />}
          <div
            className={`${
              isUserSignedIn &&
              !isCompactView &&
              (sideMenuBehavior === SideMenuBehavior.PINNED
                ? "pl-[320px]"
                : "pl-[56px]")
            } flex-1 flex flex-col transition-[padding] duration-300 relative`}
          >
            {/* Header with clean gradient background */}
            <div className="flex flex-col relative bg-gradient-hero-dark">
              <div className="flex flex-row items-center w-full relative z-10">
                {/* Left hamburger only for logged-in users (opens sidebar) */}
                {isUserSignedIn && isCompactView && (
                  <div className="pl-4">
                    <LuMenu
                      size={34}
                      onClick={isSideMenuOpen ? closeSideMenu : openSideMenu}
                      className="cursor-pointer text-white"
                    />
                  </div>
                )}
                <WelcomeAppBar />
              </div>
              <div className="flex justify-center items-center px-8 pt-8 pb-16 md:pb-20" />
            </div>

            <div className="flex-1 flex items-center justify-center bg-dark-surface-primary px-8">
              {loading && (
                <p className="text-dark-text-secondary">Loading...</p>
              )}
              {error && (
                <div className="text-center">
                  <p className="text-red-400 mb-4">{error}</p>
                  <Link
                    to="/blog"
                    className="text-blue-400 border border-transparent hover:border-white transition-all duration-300 px-2 py-1 rounded"
                  >
                    ← Back to Blog
                  </Link>
                </div>
              )}
            </div>
            <WelcomeFooter />
          </div>
        </div>
      </>
    );
  }

  const formattedDate = new Date(meta.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const canonicalUrl = `https://darvin.dev/blog/${meta.slug}`;

  return (
    <>
      <Helmet>
        <title>{meta.title} | Darvin Blog</title>
        <meta name="description" content={meta.excerpt} />

        {/* Open Graph */}
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:image"
          content={`https://darvin.dev${meta.coverImage}`}
        />
        <meta property="article:published_time" content={meta.date} />
        <meta property="article:author" content="Darvin Team" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.excerpt} />
        <meta
          name="twitter:image"
          content={`https://darvin.dev${meta.coverImage}`}
        />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Structured Data - BlogPosting */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: meta.title,
            image: `https://darvin.dev${meta.coverImage}`,
            datePublished: meta.date,
            dateModified: meta.date,
            author: {
              "@type": "Organization",
              name: "Darvin Team",
            },
            publisher: {
              "@type": "Organization",
              name: "Darvin",
              logo: {
                "@type": "ImageObject",
                url: "https://darvin.dev/assets/icon-512.png",
              },
            },
            description: meta.excerpt,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": canonicalUrl,
            },
          })}
        </script>
      </Helmet>

      <div className="min-h-screen flex">
        {isUserSignedIn && !isCompactView && <SideMenuPanel />}
        <div
          className={`${
            isUserSignedIn &&
            !isCompactView &&
            (sideMenuBehavior === SideMenuBehavior.PINNED
              ? "pl-[320px]"
              : "pl-[56px]")
          } flex-1 flex flex-col transition-[padding] duration-300 relative`}
        >
          {/* Header with clean gradient background */}
          <div className="flex flex-col relative bg-gradient-hero-dark">
            <div className="flex flex-row items-center w-full relative z-10">
              {/* Left hamburger only for logged-in users (opens sidebar) */}
            {isUserSignedIn && isCompactView && (
              <div className="pl-4">
                <LuMenu
                  size={34}
                  onClick={isSideMenuOpen ? closeSideMenu : openSideMenu}
                  className="cursor-pointer text-white"
                />
              </div>
            )}
              <WelcomeAppBar />
            </div>

            {/* Back to Blog Link */}
            <div className="max-w-[680px] mx-auto px-6 pt-8 pb-4 w-full relative z-10">
              <Link
                to="/blog"
                className="font-[600] text-white border border-transparent hover:border-white transition-all duration-300 px-2 py-1 rounded inline-flex items-center gap-1"
              >
                ← Back to Blog
              </Link>
            </div>

            {/* Article Header */}
            <div className="max-w-[680px] mx-auto px-6 pb-8 md:pb-12 w-full relative z-10">
              {/* Meta Info */}
              <div className="mb-6">
                <p className="text-sm text-white/70 mb-2">
                  {formattedDate} • by Darvin Team
                </p>
                <h1 className={`${typography.size.hero.mobile} lg:${typography.size.hero.desktop} font-bold text-white mb-6`}>
                  {meta.title}
                </h1>
              </div>
            </div>

            {/* Fade transition overlay */}
            <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none bg-gradient-to-b from-transparent via-dark-surface-primary/60 to-dark-surface-primary z-10" />
          </div>

          {/* Article Content */}
          <div className="flex-1 flex flex-col bg-dark-surface-primary">
            <div className="bg-dark-surface-primary">
              {/* Cover Image */}
              <div className="max-w-[680px] mx-auto px-6 pb-8">
                <div className="mb-6">
                  <img
                    src={meta.coverImage}
                    alt={meta.title}
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              </div>

              {/* Article Text Content */}
              <div className="max-w-[680px] mx-auto px-6 pb-12">
                <div
                  className="prose prose-invert max-w-none
                  prose-headings:text-white prose-headings:font-bold
                  prose-h2:text-[28px] prose-h2:leading-[1.3] prose-h2:mt-12 prose-h2:mb-4
                  prose-h3:text-[21px] prose-h3:leading-[1.4] prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-[20px] prose-p:leading-[34px] prose-p:text-dark-text-secondary prose-p:mb-8
                  prose-a:text-blue-400
                  prose-strong:text-white prose-strong:font-semibold
                  prose-ul:my-8 prose-li:text-[20px] prose-li:leading-[34px] prose-li:my-2 prose-li:text-dark-text-secondary
                  prose-code:text-white prose-code:text-[18px]
                  prose-pre:bg-gray-900 prose-pre:text-[16px]
                  prose-img:rounded-lg prose-img:shadow-lg prose-img:my-8
                "
                >
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => (
                        <p className="text-[20px] leading-[34px] text-dark-text-secondary mb-8">
                          {children}
                        </p>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-[28px] leading-[1.3] font-bold mt-12 mb-4">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-[21px] leading-[1.4] font-bold mt-8 mb-3">
                          {children}
                        </h3>
                      ),
                      ul: ({ children }) => (
                        <ul className="my-8 space-y-2 list-disc pl-6">
                          {children}
                        </ul>
                      ),
                      ol: ({ children }) => (
                        <ol className="my-8 space-y-2 list-decimal pl-6">
                          {children}
                        </ol>
                      ),
                      li: ({ children }) => (
                        <li className="text-[20px] leading-[34px] text-dark-text-secondary ml-2">
                          {children}
                        </li>
                      ),
                      pre: ({ children }) => {
                        const codeText = extractTextFromChildren(children);
                        const isCopied = copiedCode === codeText;

                        return (
                          <div className="relative group my-8">
                            <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                              {children}
                            </pre>
                            <button
                              onClick={() => handleCopyCode(codeText)}
                              className="absolute top-2 right-2 p-2 rounded bg-gray-800 hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
                              title={isCopied ? "Copied!" : "Copy to clipboard"}
                            >
                              {isCopied ? (
                                <span className="text-green-400 text-sm">
                                  ✓ Copied
                                </span>
                              ) : (
                                <span className="text-gray-300 text-sm">
                                  📋 Copy
                                </span>
                              )}
                            </button>
                          </div>
                        );
                      },
                      code: ({ inline, children }: any) =>
                        inline ? (
                          <code className="bg-gray-800 px-1.5 py-0.5 rounded text-[16px] font-mono">
                            {children}
                          </code>
                        ) : (
                          <code className="text-[14px] font-mono text-gray-200">
                            {children}
                          </code>
                        ),
                    }}
                  >
                    {content}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          </div>

          <WelcomeFooter />
        </div>
      </div>
    </>
  );
}
